"use client";

import { Button } from "@padi/ui/button";
import { DialogSuite } from "@padi/suites/dialog";
import { CropDetail, useOrderCrop } from "../_hooks/";
import { FormInput } from "@padi/forms/form-input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import { Form } from "@padi/ui/form";
import { useAuth } from "@padi/lib/hooks/use-auth";
import { useLocalStorage } from "@padi/lib/hooks/use-local-storage";
import { useRouter, usePathname } from "next/navigation";
import { ROUTES } from "@padi/lib/constants";
import { toast } from "sonner";
import { useState } from "react";
import { fDMoney, fTon, UNIT } from "@padi/lib/utils/format";

interface OrderCropFormProps {
  crop: CropDetail;
  handleClose: () => void;
  children?: React.ReactNode;
}

export function OrderCropForm({ crop, handleClose }: OrderCropFormProps) {
  const { mutate: orderCrop, isPending: ordering } = useOrderCrop();

  const schema = z.object({
    quantity: z.coerce.number<number>(),
  });

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { quantity: crop?.minOrderInTon },
  });

  // const quantity = form.watch("quantity");
  // const total = (Number(quantity) || 0) * crop.pricePerTon;

  const onSubmit = form.handleSubmit((values) => {
    orderCrop(
      {
        listingId: crop.id,
        quantityInTon: values.quantity,
      },
      {
        onSuccess: handleClose,
      },
    );
  });
  const { displayMoney } = crop;

  return (
    <Form {...form}>
      <form className="space-y-4" onSubmit={onSubmit}>
        <div className="flex justify-between text-sm font-medium">
          <div>
            <p className="text-muted-foreground">{fTon("Price", " per ")}</p>
            <p>{fDMoney(displayMoney.pricePerTon)}</p>
          </div>
          <div className="text-right">
            <p className="text-muted-foreground">Available</p>
            <p>{fTon(crop.quantityInTon)}</p>
          </div>
        </div>

        <FormInput
          name="quantity"
          label={`Quantity (${UNIT})`}
          type="number"
          min={crop.minOrderInTon}
          max={crop.quantityInTon}
        />

        {/* <div className="pt-2 border-t border-dashed flex justify-between text-sm">
          <span className="text-muted-foreground">Total</span>
          <span className="font-medium">
            {displayMoney.pricePerTon.usd.amount * quantity}
          </span>
        </div> */}
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="secondary" type="submit" disabled={ordering}>
            {ordering ? "Placing Order..." : "Confirm Order"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
interface OrderCropDialogProps {
  crop: CropDetail;
  children?: React.ReactNode;
}
export function OrderCropDialog({ crop, children }: OrderCropDialogProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const [, setAuthRedirect] = useLocalStorage("authRedirect", "/home");
  const [open, setOpen] = useState(false);

  const handleOpenChange = (newOpen: boolean) => {
    if (isLoading) return;

    if (newOpen && !isAuthenticated) {
      toast.info("Login to continue");
      setAuthRedirect(pathname);
      router.push(ROUTES.SIGNIN);
      return;
    }

    setOpen(newOpen);
  };

  return (
    <DialogSuite
      trigger={children}
      title="Order Crop"
      description="Fill in the details below to place your order."
      contentClassName="sm:max-w-lg"
      open={open}
      onOpenChange={handleOpenChange}
    >
      <OrderCropForm crop={crop} handleClose={() => setOpen(false)} />
    </DialogSuite>
  );
}
